export type StageDrawing = 'site' | 'massing' | 'section' | 'scaffold' | 'plan' | 'sensors'

export interface ProcessStage {
    id: string
    number: string
    title: string
    duration: string
    months: number | null
    summary: string
    measured: string
    deliverables: string
    people: string
    drawing: StageDrawing
    drawingLabel: string
}

export const PROCESS_STAGES: Array<ProcessStage> = [
    {
        id: 'brief-and-survey',
        number: '01',
        title: 'Brief and survey',
        duration: '2 months',
        months: 2,
        summary: 'We agree what the building must do, then scan the site and anything standing on it before a line is drawn.',
        measured: 'Terrestrial and drone lidar at 2 mm, levels, trees, utilities and neighbouring facades',
        deliverables: 'Registered point cloud, site model, brief and carbon target',
        people: 'Client, lead partner, two surveyors',
        drawing: 'site',
        drawingLabel: 'Line drawing of a site boundary with contour lines, trees and a north arrow.'
    },
    {
        id: 'concept',
        number: '02',
        title: 'Concept',
        duration: '3 months',
        months: 3,
        summary: 'Massing options are tested on the scanned site for daylight, wind, cost and embodied carbon.',
        measured: 'Daylight and wind studies on the scanned terrain, carbon budget per option',
        deliverables: 'Two or three massing options, a recommended concept, cost range',
        people: 'Client, architects, building physicist, cost consultant',
        drawing: 'massing',
        drawingLabel: 'Line drawing of building masses stepping up on a site plate.'
    },
    {
        id: 'developed-design',
        number: '03',
        title: 'Developed design',
        duration: '6 months',
        months: 6,
        summary: 'Architecture, structure and services are designed in one federated model, rebuilt every night.',
        measured: 'Thermal and structural models, clash checks at 5 mm, sensor positions planned',
        deliverables: 'Planning drawings, specification, thermal twin, tender package',
        people: 'Architects, structural and services engineers, planners',
        drawing: 'section',
        drawingLabel: 'Line drawing of a building section with three floors and a stepped roof.'
    },
    {
        id: 'construction',
        number: '04',
        title: 'Construction',
        duration: '18 months',
        months: 18,
        summary: 'Every structural stage is scanned on site and checked against the model before the next one starts.',
        measured: 'Weekly scans of each structural stage, deviation reports within 48 hours',
        deliverables: 'Site deviation reports, approved changes, construction record',
        people: 'Contractor, site architect, surveyors, structural engineer',
        drawing: 'scaffold',
        drawingLabel: 'Line drawing of a scaffold frame with bracing around a rising structure.'
    },
    {
        id: 'handover',
        number: '05',
        title: 'Handover',
        duration: '1 month',
        months: 1,
        summary: 'A full as-built scan becomes the starting record, and every sensor is commissioned and tested.',
        measured: 'As-built scan of the finished building, sensor commissioning, final deviation report',
        deliverables: 'As-built model, operating manual, live dashboard',
        people: 'Client, facilities team, contractor, commissioning engineer',
        drawing: 'plan',
        drawingLabel: 'Line drawing of a finished floor plan with rooms, doors and stairs.'
    },
    {
        id: 'monitoring',
        number: '06',
        title: 'Monitoring',
        duration: 'Years 1 to 10',
        months: null,
        summary: 'The building reports back for ten years: we read it every month and audit it every year.',
        measured: '38 sensors per 1 000 m², readings every 60 seconds, an annual re-scan',
        deliverables: 'Monthly reports, annual audit, lessons carried into the next building',
        people: 'Facilities team, building physicist, lead partner',
        drawing: 'sensors',
        drawingLabel: 'Line drawing of a floor plan with a network of sensor nodes joined by data lines.'
    }
]

export const MONTHS_TO_HANDOVER = PROCESS_STAGES.reduce((sum, stage) => sum + (stage.months ?? 0), 0)

export const MONITORING_YEARS = 10
